import supabase from '../lib/supabase';

/**
 * Authentication service for HelmPlanner with Supabase integration
 */
export default class AuthService {
  /**
   * Login user with email and password
   */
  static async login(email, password) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      // Verify we have a user
      if (!data.user) {
        throw new Error('Keine Benutzerdaten erhalten');
      }

      // Get user profile and departments
      const authData = await this.checkAuth();
      if (!authData || !authData.user) {
        throw new Error('Benutzerprofil konnte nicht geladen werden');
      }

      return authData;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  /**
   * Logout user
   */
  static async logout() {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      localStorage.removeItem('currentDepartmentId');
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }

  /**
   * Check if user is authenticated and get user data
   */
  static async checkAuth() {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      
      if (error || !user) {
        return null;
      }

      // Get user profile with better error handling
      const { data: profile, error: profileError } = await supabase
        .from('users_helm')
        .select('*')
        .eq('id', user.id)
        .single();

      // If profile doesn't exist, return null instead of throwing an error
      if (profileError) {
        console.error('Profile error:', profileError);
        return null;
      }

      // Ensure we have a valid profile object
      if (!profile || Object.keys(profile).length === 0) {
        console.error('Empty or invalid profile data');
        return null;
      }

      // Get user departments with better error handling
      const { data: departments, error: deptError } = await supabase
        .from('user_departments_helm')
        .select(`
          departments_helm (
            id,
            name,
            description
          )
        `)
        .eq('user_id', user.id);

      let userDepartments = [];
      if (deptError) {
        console.error('Departments error:', deptError);
      } else {
        userDepartments = departments?.map(d => d.departments_helm) || [];
      }

      return {
        user: profile,
        departments: userDepartments
      };
    } catch (error) {
      console.error('Auth check error:', error);
      return null;
    }
  }

  /**
   * Setup initial admin user and department
   */
  static async setupInitialAdmin(adminData) {
    try {
      console.log('Setting up initial admin...');

      // Validate required fields
      if (!adminData.email || !adminData.password || !adminData.full_name || !adminData.department_name) {
        throw new Error('E-Mail, Passwort, Name und Abteilungsname sind erforderlich');
      }

      // Check if any users already exist
      const { data: existingUsers, error: checkError } = await supabase
        .from('users_helm')
        .select('id')
        .limit(1);

      if (checkError && checkError.code !== 'PGRST116') {
        console.error('Error checking existing users:', checkError);
        // Continue anyway, as this might be a fresh setup
      }

      if (existingUsers && existingUsers.length > 0) {
        throw new Error('Es existieren bereits Benutzer im System. Die Ersteinrichtung ist nicht mehr möglich.');
      }

      // Step 1: Create admin user in Supabase Auth
      console.log('Creating auth user...');
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: adminData.email,
        password: adminData.password,
        options: {
          data: {
            full_name: adminData.full_name
          }
        }
      });

      if (authError) {
        console.error('Auth creation error:', authError);
        throw new Error('Fehler beim Erstellen des Benutzerkontos: ' + authError.message);
      }

      if (!authData.user) {
        throw new Error('Administrator-Konto konnte nicht erstellt werden');
      }

      // Step 2: Wait for auth user to be created
      console.log('Waiting for auth user creation...');
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Step 3: Create department first (no user dependency)
      console.log('Creating department...');
      const { data: department, error: deptError } = await supabase
        .from('departments_helm')
        .insert({
          name: adminData.department_name,
          description: adminData.department_description || null
        })
        .select()
        .single();

      if (deptError) {
        console.error('Department creation error:', deptError);
        throw new Error('Fehler beim Erstellen der Abteilung: ' + deptError.message);
      }

      // Step 4: Create admin profile manually with RLS bypass
      console.log('Creating admin profile...');
      
      // Use service role to bypass RLS for initial setup
      const { data: profile, error: profileError } = await supabase.rpc('create_initial_admin', {
        admin_id: authData.user.id,
        admin_email: adminData.email,
        admin_name: adminData.full_name
      });

      if (profileError) {
        console.error('Profile creation error:', profileError);
        
        // Fallback: Try direct insert
        const { data: fallbackProfile, error: fallbackError } = await supabase
          .from('users_helm')
          .insert({
            id: authData.user.id,
            email: adminData.email,
            full_name: adminData.full_name,
            role: 'admin'
          })
          .select()
          .single();

        if (fallbackError) {
          throw new Error('Fehler beim Erstellen des Benutzerprofils: ' + fallbackError.message);
        }
      }

      // Step 5: Link admin to department
      console.log('Linking admin to department...');
      const { error: relationError } = await supabase
        .from('user_departments_helm')
        .insert({
          user_id: authData.user.id,
          department_id: department.id
        });

      if (relationError) {
        console.error('Relation creation error:', relationError);
        throw new Error('Fehler beim Verknüpfen mit der Abteilung: ' + relationError.message);
      }

      return {
        success: true,
        message: 'Administrator-Account und Abteilung erfolgreich erstellt',
        admin: {
          id: authData.user.id,
          email: adminData.email,
          full_name: adminData.full_name,
          role: 'admin'
        },
        department: department
      };

    } catch (error) {
      console.error('Error setting up initial admin:', error);
      return {
        success: false,
        message: error.message || 'Unbekannter Fehler bei der Ersteinrichtung',
        admin: null,
        department: null
      };
    }
  }

  /**
   * Create new user (Admin only)
   */
  static async createUser(userData) {
    try {
      // Validate required fields
      if (!userData.email || !userData.password || !userData.full_name) {
        throw new Error('E-Mail, Passwort und vollständiger Name sind erforderlich');
      }

      // Create user in Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password,
        options: {
          data: {
            full_name: userData.full_name
          }
        }
      });

      if (authError) throw authError;

      if (!authData.user) {
        throw new Error('Benutzer konnte nicht erstellt werden');
      }

      // Wait a moment for the auth user to be created
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Create user profile
      const { data: profile, error: profileError } = await supabase
        .from('users_helm')
        .insert({
          id: authData.user.id,
          email: userData.email,
          full_name: userData.full_name,
          role: userData.role || 'user'
        })
        .select()
        .single();

      if (profileError) throw profileError;

      // Add department relationships
      if (userData.department_ids && userData.department_ids.length > 0) {
        const departmentRelations = userData.department_ids.map(deptId => ({
          user_id: authData.user.id,
          department_id: deptId
        }));

        const { error: relationError } = await supabase
          .from('user_departments_helm')
          .insert(departmentRelations);

        if (relationError) throw relationError;
      }

      return profile;
    } catch (error) {
      console.error('Create user error:', error);
      throw error;
    }
  }

  /**
   * Update user (Admin only)
   */
  static async updateUser(userId, userData) {
    try {
      if (!userId) {
        throw new Error('Benutzer-ID ist erforderlich');
      }

      // Update user profile
      const { error: profileError } = await supabase
        .from('users_helm')
        .update({
          full_name: userData.full_name,
          role: userData.role
        })
        .eq('id', userId);

      if (profileError) throw profileError;

      // Update password if provided
      if (userData.password) {
        const { error: passwordError } = await supabase.auth.updateUser({
          password: userData.password
        });
        if (passwordError) throw passwordError;
      }

      // Update department relationships
      if (userData.department_ids) {
        // Delete existing relationships
        await supabase
          .from('user_departments_helm')
          .delete()
          .eq('user_id', userId);

        // Insert new relationships
        if (userData.department_ids.length > 0) {
          const departmentRelations = userData.department_ids.map(deptId => ({
            user_id: userId,
            department_id: deptId
          }));

          const { error: relationError } = await supabase
            .from('user_departments_helm')
            .insert(departmentRelations);

          if (relationError) throw relationError;
        }
      }

      return true;
    } catch (error) {
      console.error('Update user error:', error);
      throw error;
    }
  }

  /**
   * Delete user (Admin only)
   */
  static async deleteUser(userId) {
    try {
      if (!userId) {
        throw new Error('Benutzer-ID ist erforderlich');
      }

      // Delete user profile first
      const { error: profileError } = await supabase
        .from('users_helm')
        .delete()
        .eq('id', userId);

      if (profileError) throw profileError;

      return true;
    } catch (error) {
      console.error('Delete user error:', error);
      throw error;
    }
  }

  /**
   * Get all users (Admin only)
   */
  static async getAllUsers() {
    try {
      const { data: users, error } = await supabase
        .from('users_helm')
        .select(`
          *,
          user_departments_helm (
            departments_helm (
              id,
              name,
              description
            )
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return users.map(user => ({
        ...user,
        departments: user.user_departments_helm?.map(rel => rel.departments_helm) || []
      }));
    } catch (error) {
      console.error('Get all users error:', error);
      throw error;
    }
  }

  /**
   * Get all departments
   */
  static async getAllDepartments() {
    try {
      const { data: departments, error } = await supabase
        .from('departments_helm')
        .select('*')
        .order('name');

      if (error) throw error;

      return departments || [];
    } catch (error) {
      console.error('Get all departments error:', error);
      throw error;
    }
  }

  /**
   * Create department (Admin only)
   */
  static async createDepartment(departmentData) {
    try {
      if (!departmentData.name) {
        throw new Error('Abteilungsname ist erforderlich');
      }

      const { data, error } = await supabase
        .from('departments_helm')
        .insert({
          name: departmentData.name,
          description: departmentData.description || null
        })
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Create department error:', error);
      throw error;
    }
  }

  /**
   * Update department (Admin only)
   */
  static async updateDepartment(departmentId, departmentData) {
    try {
      if (!departmentId) {
        throw new Error('Abteilungs-ID ist erforderlich');
      }

      const { data, error } = await supabase
        .from('departments_helm')
        .update({
          name: departmentData.name,
          description: departmentData.description || null
        })
        .eq('id', departmentId)
        .select()
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Update department error:', error);
      throw error;
    }
  }

  /**
   * Delete department (Admin only)
   */
  static async deleteDepartment(departmentId) {
    try {
      if (!departmentId) {
        throw new Error('Abteilungs-ID ist erforderlich');
      }

      const { error } = await supabase
        .from('departments_helm')
        .delete()
        .eq('id', departmentId);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Delete department error:', error);
      throw error;
    }
  }
}